import { useEffect, useMemo, useRef, useState } from "react";

import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import SearchableSelect from "./SearchableSelect";
import {
  getWeeklyReportsPath,
  getSponsorQueriesPath,
  getMonitorFindingsPath,
  getCollectionPath,
  getReferralRecipientsPath,
  getReferralSubmissionsPath,
} from "../services/firestorePaths";
import { fetchActiveCoordinators, fetchCoordinators } from "../services/coordinatorService";
import {
  hasOwnCoordinatorName,
  reportCoordinatorName,
  coordinatorGroupKey,
} from "../services/weeklyReportCoordinator";
import SubmittedReportsPanel from "./weeklyReport/SubmittedReportsPanel";
import GoalPointApprovals from "./goals/GoalPointApprovals";
import MonthClosePrompt from "./goals/MonthClosePrompt";
import { reportPeriodCounts } from "../services/weeklyReportModel";
import { useLocation, useNavigate } from "react-router-dom";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import { backTarget } from "../utils/backTarget";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  CalendarDays,
  X,
  Plus,
  Save,
  ChevronDown,
  FileText,
  Sparkles,
  Layers,
  ListChecks,
  Users,
  Percent,
} from "lucide-react";

/* ------------------ CLICKABLE X AXIS ------------------ */
const ClickableXAxisTick = ({ x, y, payload, onClick }) => (
  <g transform={`translate(${x},${y})`} style={{ cursor: "pointer" }}>
    <text
      dy={16}
      textAnchor="middle"
      fill="#c4b5fd"
      fontSize={11}
      pointerEvents="all"
      onClick={() => onClick(payload.value)}
    >
      {payload.value}
    </text>
  </g>
);

/* ------------------ DATE HELPERS ------------------ */
const getDateRangeFromISOWeek = (year, week) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = simple;
  if (dayOfWeek <= 4)
    isoWeekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
  else
    isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
  const isoWeekEnd = new Date(isoWeekStart);
  isoWeekEnd.setUTCDate(isoWeekStart.getUTCDate() + 6);
  return { start: isoWeekStart, end: isoWeekEnd };
};

const formatDate = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const parseWeekKey = (week) => {
  const [year, wk] = week.split("-W");
  return { year: +year, week: +wk };
};

const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const toMMDDYYYY = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split("-");
  return `${m}/${d}/${y}`;
};

const parseMMDDYYYY = (str) => {
  if (!str) return null;
  const [m, d, y] = str.split("/").map(Number);
  return new Date(y, m - 1, d);
};

/* ------------------ SHARED UI ------------------ */
const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img src="/bg_ai.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const tooltipStyles = {
  contentStyle: {
    background: "rgba(11, 8, 32, 0.92)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "12px",
    backdropFilter: "blur(14px)",
    color: "#e2e8f0",
    fontSize: "12px",
    boxShadow: "0 10px 30px -10px rgba(124,58,237,0.5)",
  },
  itemStyle: { color: "#e2e8f0" },
  labelStyle: { color: "#c4b5fd", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: "4px" },
  cursor: { fill: "rgba(139,92,246,0.08)" },
};

const axisStyle = {
  stroke: "rgba(255,255,255,0.15)",
  tick: { fill: "rgba(226,232,240,0.7)", fontSize: 11 },
};

const Card = ({ title, subtitle, icon, children, className = "" }) => (
  <div className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-4 overflow-hidden ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
    <div className="relative">
      {(title || icon) && (
        <div className="mb-3 flex items-center gap-2">
          {icon && (
            <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            {title && <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">{title}</h3>}
            {subtitle && <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{subtitle}</p>}
          </div>
          <span className="ml-auto h-px flex-1 bg-white/5" />
        </div>
      )}
      {children}
    </div>
  </div>
);

// Prominent section heading rendered OUTSIDE cards so each block on the page is
// clearly labeled and visually separated.
const SectionHeading = ({ icon, title, hint }) => (
  <div className="flex items-center gap-2.5 pt-2">
    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500/25 via-indigo-500/25 to-blue-500/25 border border-white/15 ring-1 ring-white/10 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <h2 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight">
        {title}
      </h2>
      {hint && <p className="text-[10px] text-slate-400 leading-tight">{hint}</p>}
    </div>
    <span className="ml-2 h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
  </div>
);

const KpiTile = ({ label, value, icon, accent = "from-purple-500/20 to-indigo-500/20", valueClass = "text-white" }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-purple-300/30 bg-white/[0.03] backdrop-blur-xl px-3.5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)] group">
    <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 pointer-events-none`} />
    <div className="relative flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80 font-medium">{label}</div>
        <div className={`text-2xl font-bold tracking-tight leading-tight truncate ${valueClass}`}>{value}</div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition shrink-0">
        {icon}
      </div>
    </div>
  </div>
);

const TrendBadge = ({ trend }) => {
  if (trend === "↑")
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-300">
        <TrendingUp className="w-3 h-3" /> Up
      </span>
    );
  if (trend === "↓")
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-rose-300">
        <TrendingDown className="w-3 h-3" /> Down
      </span>
    );
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-400">
      <Minus className="w-3 h-3" /> Flat
    </span>
  );
};

/* ================== REFERRAL LINK FUNNEL (additive) ==================
   Read-only view over `referral_recipients`. Measures the questionnaire-link
   funnel — Sent → Opened → Submitted — and specifically surfaces patients who
   OPENED the link but never SUBMITTED it, with time-from-send-to-open. Purely
   additive analytics: it reads the collection and computes; it writes nothing
   and does not affect any existing report/workflow. Open data only exists for
   links opened after open-logging went live (forward-looking). */

// Firestore Timestamp | {seconds} | ISO string | ms → Date (or null).
const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The moment a recipient FIRST entered the funnel — a recipient can be
// texted more than once (the initial 48h-before-appointment text, then a
// later "forgot your questionnaire?" follow-up if they haven't submitted; see
// referralSchedule.js / REFERRAL_SCHEDULE_IMPORT.md), but "Sent" counts each
// PERSON once, on the day they were first reached — a reminder doesn't add a
// second "sent" for someone already counted. This is deliberate: "Sent" is
// the denominator for a submission success rate (how many people did we
// reach vs. how many came back), so it has to count people, not messages.
//   • `smsSentAt`             — stamped on immediate/manual sends (ReferralPage)
//   • `sends.<window>.sentAt` — stamped by the scheduled reminder cron, one
//                               entry per reminder window (initial, follow-up)
// No `createdAt` fallback: a recipient row being created isn't the same as a
// text actually going out, and counting it as "sent" overstated the number.
const earliestSentAt = (row) => {
  const times = [];
  const sends = row?.sends || {};
  for (const s of Object.values(sends)) {
    const d = toDateSafe(s?.sentAt);
    if (d) times.push(d.getTime());
  }
  const sms = toDateSafe(row?.smsSentAt);
  if (sms) times.push(sms.getTime());
  return times.length ? new Date(Math.min(...times)) : null;
};

// Open/engagement tracking is forward-looking: opens are only recorded from the
// day this feature went live. To keep the funnel honest (opens can't exist for
// older links), it counts only links SENT on/after this date. Questionnaires
// submitted BEFORE this date are shown as a historical baseline, not mixed into
// the funnel. Set this to your actual go-live date if you deploy another day.
const FUNNEL_TRACKING_START = new Date("2026-07-21T00:00:00");

const fmtStartDate = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// One data point per calendar week, for the trend chart below. "Sent" buckets
// each recipient ONCE, by the week of their earliest text (a reminder later
// doesn't add a second "sent"). "Submitted" is bucketed by each submission's
// own completion date, independent of when that person was first sent to —
// no cohort-chaining between the two. Weeks are keyed the same way as the
// site-enrollment trend (`YYYY-Www`) so the existing parseWeekKey/getISOWeek
// helpers apply unchanged.
const buildWeeklyFunnelTrend = (recipientRows, submissionRows, startMs, dateBounds) => {
  const acc = {};
  const bucketOf = (d) => `${d.getFullYear()}-W${getISOWeek(d)}`;
  const ensure = (weekKey) => {
    if (!acc[weekKey]) acc[weekKey] = { weekKey, sent: 0, opened: 0, submitted: 0 };
    return acc[weekKey];
  };
  const inRange = (d) =>
    d && (!dateBounds.from || d >= dateBounds.from) && (!dateBounds.to || d <= dateBounds.to);

  for (const r of recipientRows) {
    const sentAt = earliestSentAt(r);
    if (sentAt && inRange(sentAt)) ensure(bucketOf(sentAt)).sent += 1;
    const openedAt = toDateSafe(r.firstOpenedAt);
    if (openedAt && openedAt.getTime() >= startMs && inRange(openedAt)) {
      ensure(bucketOf(openedAt)).opened += 1;
    }
  }
  for (const s of submissionRows) {
    const submittedAt = toDateSafe(s.submittedAt);
    if (!submittedAt || !inRange(submittedAt)) continue;
    ensure(bucketOf(submittedAt)).submitted += 1;
  }

  return Object.values(acc)
    .sort((a, b) => {
      const A = parseWeekKey(a.weekKey);
      const B = parseWeekKey(b.weekKey);
      return A.year !== B.year ? A.year - B.year : A.week - B.week;
    })
    .map((w) => {
      const { year, week } = parseWeekKey(w.weekKey);
      const { start } = getDateRangeFromISOWeek(year, week);
      return { ...w, week: `W${week} '${String(year).slice(2)}`, weekStart: start };
    });
};

const pct = (num, denom) => (denom > 0 ? (num / denom) * 100 : null);
const fmtPct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

const ReferralLinkFunnelCard = () => {
  const [rows, setRows] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [recipSnap, subSnap] = await Promise.all([
          getDocs(collection(db, getReferralRecipientsPath())),
          getDocs(collection(db, getReferralSubmissionsPath())),
        ]);
        if (cancelled) return;
        setRows(recipSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSubmissions(subSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (!cancelled) setErr("Could not load referral link data.");
        console.warn("Referral funnel load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Restricts every count below to this window. Empty = all-time. Every
  // metric is filtered independently by its OWN date field (when a text was
  // sent, when a link was opened, when a form was submitted) — none of them
  // are chained through "when was this person first touched," so a
  // recipient's reminder text and an unrelated person's submission on the
  // same day both count toward that day, on their own terms.
  const dateBounds = useMemo(
    () => ({
      from: fromDate ? new Date(fromDate + "T00:00:00") : null,
      to: toDate ? new Date(toDate + "T23:59:59") : null,
    }),
    [fromDate, toDate]
  );

  const stats = useMemo(() => {
    const startMs = FUNNEL_TRACKING_START.getTime();
    const { from, to } = dateBounds;
    const inRange = (d) => d && (!from || d >= from) && (!to || d <= to);

    // Sent: each PERSON counted once, on the day they were FIRST texted — a
    // later reminder doesn't add a second "sent." This is the denominator for
    // "how many people did we reach vs. how many came back," so it has to be
    // a headcount, not a message count.
    let sent = 0;
    let sentNotOpened = 0;
    for (const r of rows) {
      const sentAt = earliestSentAt(r);
      if (!inRange(sentAt)) continue;
      sent += 1;
      if (!toDateSafe(r.firstOpenedAt)) sentNotOpened += 1;
    }

    // Opened: unique recipients whose firstOpenedAt falls in the window.
    // Still floored at FUNNEL_TRACKING_START — open-tracking didn't exist
    // before that date, so an "opened" count from before it would be bogus.
    let opened = 0;
    let openedNotSubmitted = 0;
    for (const r of rows) {
      const openedAt = toDateSafe(r.firstOpenedAt);
      if (!openedAt || openedAt.getTime() < startMs || !inRange(openedAt)) continue;
      opened += 1;
      if (r.formSubmitted !== true) openedNotSubmitted += 1;
    }

    // Submitted: real questionnaire completions in the window, straight off
    // referral_submissions.submittedAt — exactly what Raw Database's own
    // date filter counts for the same dates. Not gated by
    // FUNNEL_TRACKING_START: submission timestamps have always existed.
    let submitted = 0;
    let historicalSubmitted = 0; // kept for the baseline note below
    for (const s of submissions) {
      const submittedAt = toDateSafe(s.submittedAt);
      if (submittedAt && submittedAt.getTime() < startMs) historicalSubmitted += 1;
      if (submittedAt && inRange(submittedAt)) submitted += 1;
    }

    return {
      sent,
      opened,
      submitted,
      sentNotOpened,
      openedNotSubmitted,
      historicalSubmitted,
      openRate: pct(opened, sent),
      submitRate: pct(submitted, sent),
      submitOfOpenedRate: pct(submitted, opened),
    };
  }, [rows, submissions, dateBounds]);

  const trend = useMemo(
    () => buildWeeklyFunnelTrend(rows, submissions, FUNNEL_TRACKING_START.getTime(), dateBounds),
    [rows, submissions, dateBounds]
  );

  const dateInputCls =
    "w-full pl-8 pr-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-slate-100 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]";

  return (
    <Card>
      {loading ? (
        <p className="text-xs text-slate-400 py-3">Loading link activity…</p>
      ) : err ? (
        <p className="text-xs text-rose-300 py-3">{err}</p>
      ) : (
        <>
          {/* Baseline note: "Opened" tracking is forward-looking (opens can't
              exist for links sent before this went live), so Opened / Sent ·
              Not Opened / Opened · Not Submitted only reflect activity from
              this date on. Sent and Submitted have no such floor — those
              timestamps have always existed. */}
          <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px]">
            <span className="inline-flex items-center gap-1.5 font-semibold text-slate-200">
              <Calendar className="w-3.5 h-3.5 text-purple-300" />
              Open tracking started {fmtStartDate(FUNNEL_TRACKING_START)}
            </span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-400">
              <span className="font-semibold text-emerald-300">
                {stats.historicalSubmitted}
              </span>{" "}
              questionnaire{stats.historicalSubmitted === 1 ? "" : "s"} submitted
              before this date (informational only — "Submitted" below is not
              floored by it, "Opened" is)
            </span>
          </div>

          {/* Date-range filter. "Sent" counts each person once, by the day
              they were FIRST texted; "Opened" by first-open date; "Submitted"
              by completion date, independent of when that person was first
              texted (so it isn't limited to only the people counted in
              "Sent" for the same window). Doesn't touch any workflow; it's a
              read-only lens over the same data. */}
          <div className="mb-3 flex items-end gap-2 flex-wrap">
            <div className="min-w-[130px]">
              <p className="text-[10px] text-slate-500 mb-1">From</p>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500 pointer-events-none" />
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={dateInputCls} />
              </div>
            </div>
            <div className="min-w-[130px]">
              <p className="text-[10px] text-slate-500 mb-1">To</p>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500 pointer-events-none" />
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={dateInputCls} />
              </div>
            </div>
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(""); setToDate(""); }}
                className="text-[11px] text-purple-300 hover:text-purple-200 transition pb-1.5"
              >
                Clear range
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            <KpiTile
              label="Sent"
              value={stats.sent}
              icon={<TrendingUp className="w-4 h-4 text-purple-200" />}
            />
            <KpiTile
              label="Opened"
              value={stats.opened}
              icon={<Activity className="w-4 h-4 text-sky-200" />}
              accent="from-sky-500/20 to-blue-500/20"
            />
            <KpiTile
              label="Submitted"
              value={stats.submitted}
              icon={<CheckCircle2 className="w-4 h-4 text-emerald-200" />}
              accent="from-emerald-500/20 to-teal-500/20"
              valueClass="text-emerald-200"
            />
            <KpiTile
              label="Sent · Not Opened"
              value={stats.sentNotOpened}
              icon={<X className="w-4 h-4 text-rose-200" />}
              accent="from-rose-500/20 to-pink-500/20"
              valueClass="text-rose-200"
            />
            <KpiTile
              label="Opened · Not Submitted"
              value={stats.openedNotSubmitted}
              icon={<AlertTriangle className="w-4 h-4 text-amber-200" />}
              accent="from-amber-500/20 to-orange-500/20"
              valueClass="text-amber-200"
            />
          </div>

          {/* Conversion rates — same three counts above, expressed as %. */}
          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <KpiTile
              label="Open Rate"
              value={fmtPct(stats.openRate)}
              icon={<Percent className="w-4 h-4 text-sky-200" />}
              accent="from-sky-500/20 to-blue-500/20"
            />
            <KpiTile
              label="Submit Rate (of Sent)"
              value={fmtPct(stats.submitRate)}
              icon={<Percent className="w-4 h-4 text-emerald-200" />}
              accent="from-emerald-500/20 to-teal-500/20"
              valueClass="text-emerald-200"
            />
            <KpiTile
              label="Submit Rate (of Opened)"
              value={fmtPct(stats.submitOfOpenedRate)}
              icon={<Percent className="w-4 h-4 text-amber-200" />}
              accent="from-amber-500/20 to-orange-500/20"
              valueClass="text-amber-200"
            />
          </div>

          {/* Weekly trend — same Sent/Opened/Submitted counts, over time. */}
          {trend.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">
                Weekly trend
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="week" stroke={axisStyle.stroke} tick={axisStyle.tick} />
                  <YAxis allowDecimals={false} {...axisStyle} />
                  <Tooltip {...tooltipStyles} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#cbd5e1" }} />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    name="Sent"
                    stroke="#A78BFA"
                    strokeWidth={2}
                    dot={{ fill: "#A78BFA", r: 2.5, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="opened"
                    name="Opened"
                    stroke="#38BDF8"
                    strokeWidth={2}
                    dot={{ fill: "#38BDF8", r: 2.5, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="submitted"
                    name="Submitted"
                    stroke="#34D399"
                    strokeWidth={2}
                    dot={{ fill: "#34D399", r: 2.5, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

/* ================== MAIN COMPONENT ================== */
const AdminReportsPage = () => {
  const navigate = useNavigate();
  // UI revamp: the home page's "Goal points to approve" link scrolls to that section.
  const location = useLocation();
  const goalPointsFocus = UI_REVAMP_ENABLED && location.state?.section === "goal-points";
  const [reports, setReports] = useState([]);
  const [period, setPeriod] = useState("THIS_MONTH");
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [coordRange, setCoordRange] = useState("ALL");
  const [activeTab, setActiveTab] = useState("overview");
  const [weeklyFrom, setWeeklyFrom] = useState("");
  const [weeklyTo, setWeeklyTo] = useState("");

  const [showPerfEntry, setShowPerfEntry] = useState(false);
  const [entryType, setEntryType] = useState(null);
  const [entryCoordinator, setEntryCoordinator] = useState("");
  const [entryStudyId, setEntryStudyId] = useState("");
  const [raisedDate, setRaisedDate] = useState("");
  const [resolvedDate, setResolvedDate] = useState("");

  const [coordinatorList, setCoordinatorList] = useState([]);
  const [queries, setQueries] = useState([]);
  const [findings, setFindings] = useState([]);

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDocs(collection(db, getWeeklyReportsPath()));
      setReports(
        snap.docs.map((d) => ({
          ...d.data(),
          // Document id, kept apart from the report's own fields, so a report
          // with no coordinator name can be assigned one (see assignReport).
          __docId: d.id,
          submittedAt: d.data().submittedAt?.toDate?.() || new Date(),
        }))
      );
    };
    fetchData();
  }, []);

  // Coordinator names come from the `coordinators` collection, which is what it
  // is for. This used to read the ids of `coordinatorEnrollments`, which worked
  // only because that collection happened to be keyed by coordinator name —
  // now that enrollment counts are keyed by PERIOD, those ids are "2026-08" and
  // this page would have quietly started listing months as coordinators.
  useEffect(() => {
    const loadCoordinatorNames = async () => {
      try {
        const list = await fetchActiveCoordinators();
        setCoordinatorList(list.map((c) => c.name).filter(Boolean));
      } catch (err) {
        console.error("Failed to load coordinators:", err);
        setCoordinatorList([]);
      }
    };
    loadCoordinatorNames();
  }, []);

  // EVERY coordinator, active or not — for crediting an old report with no
  // name, which may well have been filed by someone who has since left.
  const [allCoordinatorNames, setAllCoordinatorNames] = useState([]);
  useEffect(() => {
    fetchCoordinators()
      .then((list) => {
        const active = list.filter((c) => c.active !== false).map((c) => c.name);
        const inactive = list.filter((c) => c.active === false).map((c) => c.name);
        setAllCoordinatorNames([...new Set([...active, ...inactive].map((n) => String(n || "").trim()).filter(Boolean))]);
      })
      .catch((err) => console.error("Failed to load all coordinators:", err));
  }, []);

  // Assigning a name to a report saved with none. Writes NEW fields only —
  // the report's original (blank) coordinator field is never touched.
  const [assigningId, setAssigningId] = useState(null);
  const [assignPick, setAssignPick] = useState("");
  const [assignTyped, setAssignTyped] = useState("");
  const [assignFreeText, setAssignFreeText] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState("");
  // After an assignment the selected coordinator's report list is stale;
  // re-pick it from the recomputed groups (effect below).
  const refreshSelectionRef = useRef(false);

  const startAssign = (report) => {
    setAssigningId(report.__docId);
    setAssignPick("");
    setAssignTyped("");
    setAssignFreeText(false);
    setAssignError("");
  };

  const applyAssignment = (docId, fields) => {
    refreshSelectionRef.current = true;
    setReports((prev) => prev.map((r) => (r.__docId === docId ? { ...r, ...fields } : r)));
  };

  const saveAssignment = async (report) => {
    const name = (assignFreeText ? assignTyped : assignPick).trim();
    if (!name || !report.__docId) return;
    setAssignBusy(true);
    setAssignError("");
    try {
      const by = (auth.currentUser?.email || "").toLowerCase();
      await updateDoc(doc(db, getWeeklyReportsPath(), report.__docId), {
        assignedCoordinator: name,
        assignedCoordinatorBy: by,
        assignedCoordinatorAt: serverTimestamp(),
      });
      applyAssignment(report.__docId, {
        assignedCoordinator: name,
        assignedCoordinatorBy: by,
        assignedCoordinatorAt: new Date(),
      });
      setAssigningId(null);
    } catch (err) {
      console.error("Failed to assign coordinator:", err);
      setAssignError("Couldn't save. Nothing was changed — please try again.");
    } finally {
      setAssignBusy(false);
    }
  };

  const undoAssignment = async (report) => {
    if (!report.__docId) return;
    if (!window.confirm(`Remove "${report.assignedCoordinator}" from this report? It goes back to having no name.`)) return;
    try {
      await updateDoc(doc(db, getWeeklyReportsPath(), report.__docId), {
        assignedCoordinator: deleteField(),
        assignedCoordinatorBy: deleteField(),
        assignedCoordinatorAt: deleteField(),
      });
      applyAssignment(report.__docId, {
        assignedCoordinator: undefined,
        assignedCoordinatorBy: undefined,
        assignedCoordinatorAt: undefined,
      });
    } catch (err) {
      console.error("Failed to undo assignment:", err);
      window.alert("Couldn't undo. Nothing was changed — please try again.");
    }
  };

  useEffect(() => {
    const fetchOperationalData = async () => {
      const [querySnap, findingSnap] = await Promise.all([
        getDocs(collection(db, getSponsorQueriesPath())),
        getDocs(collection(db, getMonitorFindingsPath())),
      ]);
      setQueries(querySnap.docs.map(d => d.data()));
      setFindings(findingSnap.docs.map(d => d.data()));
    };
    fetchOperationalData();
  }, []);

  /* ---------------- PERIOD FILTER ---------------- */
  const filteredReports = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const currentWeek = getISOWeek(now);

    return reports.filter((r) => {
      const d = r.submittedAt;
      if (period === "THIS_WEEK")
        return getISOWeek(d) === currentWeek && d.getFullYear() === y;
      if (period === "THIS_MONTH")
        return d.getMonth() === now.getMonth() && d.getFullYear() === y;
      if (period === "LAST_3M") {
        const cutoff = new Date(now);
        cutoff.setMonth(cutoff.getMonth() - 3);
        return d >= cutoff;
      }
      if (period === "YTD") return d.getFullYear() === y;
      return true; // ALL
    });
  }, [reports, period]);

  // Ids of the reports the time filter is showing; the patient-level lists
  // share these ids, so "Submitted reports" follows the same filter.
  const visibleReportIds = useMemo(
    () => new Set(filteredReports.map((r) => r.__docId)),
    [filteredReports]
  );

  const allStudies = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      (r.studies || []).forEach(s => { map[s.studyId] = s.name; });
    });
    return Object.entries(map).map(([studyId, name]) => ({ studyId, name }));
  }, [reports]);

  /* ---------------- SITE TREND ---------------- */
  const siteTrendData = useMemo(() => {
    const acc = {};
    filteredReports.forEach((r) => {
      const week = `${r.submittedAt.getFullYear()}-W${getISOWeek(r.submittedAt)}`;
      if (!acc[week]) acc[week] = { week, enrollments: 0 };
      acc[week].enrollments += (r.studies || []).reduce((a, s) => a + (s.enrollments || 0), 0);
    });
    return Object.values(acc).sort((a, b) => {
      const A = parseWeekKey(a.week);
      const B = parseWeekKey(b.week);
      return A.year !== B.year ? A.year - B.year : A.week - B.week;
    });
  }, [filteredReports]);

  /* ---------------- COORDINATORS ---------------- */
  const coordinators = useMemo(() => {
    const map = {};
    filteredReports.forEach((r) => {
      // assignedCoordinator is only ever set by an admin on a report that was
      // saved with NO name, and never overrides a name the report already has.
      const raw = reportCoordinatorName(r) || "UNKNOWN";
      const norm = coordinatorGroupKey(raw);
      const firestoreName = raw.trim();

      let key = Object.keys(map).find((k) => k.startsWith(norm) || norm.startsWith(k));
      if (!key) key = norm;

      if (!map[key]) {
        map[key] = {
          key,
          displayName: raw,
          firestoreName,
          enrollments: 0,
          screenings: 0,
          followUps: 0,
          preScreens: 0,
          screenFailures: 0,
          weeksSet: new Set(),
          history: [],
          reports: [],
        };
      }

      let weeklyEnrollments = 0;
      (r.studies || []).forEach((s) => {
        map[key].enrollments += s.enrollments || 0;
        map[key].screenings += s.screenings || 0;
        map[key].followUps += s.followUps || 0;
        map[key].preScreens += s.preScreens || 0;
        map[key].screenFailures += s.screenFailures || 0;
        weeklyEnrollments += s.enrollments || 0;
      });
      // New-format reports carry pre-screens / follow-ups for the whole period
      // (not per study); 0 for old reports.
      const period = reportPeriodCounts(r);
      map[key].preScreens += period.preScreens;
      map[key].followUps += period.followUps;

      const weekLabel = `${r.submittedAt.getFullYear()}-W${getISOWeek(r.submittedAt)}`;
      map[key].weeksSet.add(weekLabel);
      map[key].history.push({ week: weekLabel, enrollments: weeklyEnrollments });
      map[key].reports.push(r);
    });

    return Object.values(map).map((c) => {
      c.history.sort((a, b) => {
        const A = parseWeekKey(a.week);
        const B = parseWeekKey(b.week);
        return A.year !== B.year ? A.year - B.year : A.week - B.week;
      });

      const last4 = c.history.slice(-4).reduce((a, b) => a + b.enrollments, 0);
      const prev4 = c.history.slice(-8, -4).reduce((a, b) => a + b.enrollments, 0);
      let trend = "→";
      if (last4 > prev4) trend = "↑";
      if (last4 < prev4) trend = "↓";

      return {
        ...c,
        displayName: c.key.charAt(0).toUpperCase() + c.key.slice(1),
        weeks: c.weeksSet.size,
        trend,
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [filteredReports]);

  // Only after an assign / undo: re-pick the open coordinator from the fresh
  // groups so its report list is current (closes if the group is now empty,
  // e.g. the last "Unknown" report was assigned). Nothing else re-picks it.
  useEffect(() => {
    if (!refreshSelectionRef.current) return;
    refreshSelectionRef.current = false;
    setSelectedCoordinator((prev) => (prev ? coordinators.find((c) => c.key === prev.key) || null : prev));
  }, [coordinators]);

  /* ---------------- WEEK LOOKUP ---------------- */
  const weekLookup = useMemo(() => {
    const map = {};
    filteredReports.forEach((r) => {
      const weekKey = `${r.submittedAt.getFullYear()}-W${getISOWeek(r.submittedAt)}`;
      if (!map[weekKey]) {
        const { year, week } = parseWeekKey(weekKey);
        map[weekKey] = { year, week, range: getDateRangeFromISOWeek(year, week), reports: [], coordinators: {}, studies: {}, totalEnrollments: 0 };
      }
      const entry = map[weekKey];
      entry.reports.push(r);
      const coord = coordinatorGroupKey(reportCoordinatorName(r) || "UNKNOWN");
      (r.studies || []).forEach((s) => {
        entry.totalEnrollments += s.enrollments || 0;
        entry.coordinators[coord] = (entry.coordinators[coord] || 0) + (s.enrollments || 0);
        const key = `${s.studyId} — ${s.name}`;
        if (!entry.studies[key]) entry.studies[key] = { ...s };
        else {
          entry.studies[key].enrollments += s.enrollments || 0;
          entry.studies[key].followUps += s.followUps || 0;
        }
      });
    });
    Object.values(map).forEach((w) => (w.studies = Object.values(w.studies)));
    return map;
  }, [filteredReports]);

  /* ---------------- COORDINATOR STATS ---------------- */
  const coordinatorStats = useMemo(() => {
    if (!selectedCoordinator) return null;

    const cutoff =
      coordRange === "30" ? new Date(new Date().setDate(new Date().getDate() - 30))
      : coordRange === "90" ? new Date(new Date().setDate(new Date().getDate() - 90))
      : null;

    const filteredReps = cutoff
      ? selectedCoordinator.reports.filter((r) => r.submittedAt >= cutoff)
      : selectedCoordinator.reports;

    let enrollments = 0, screenings = 0, followUps = 0, preScreens = 0, screenFailures = 0;
    const weekly = {};

    filteredReps.forEach((r) => {
      const week = `${r.submittedAt.getFullYear()}-W${getISOWeek(r.submittedAt)}`;
      (r.studies || []).forEach((s) => {
        enrollments += s.enrollments || 0;
        screenings += s.screenings || 0;
        followUps += s.followUps || 0;
        preScreens += s.preScreens || 0;
        screenFailures += s.screenFailures || 0;
        weekly[week] = (weekly[week] || 0) + (s.enrollments || 0);
      });
      const period = reportPeriodCounts(r); // report-level, not per study
      preScreens += period.preScreens;
      followUps += period.followUps;
    });

    const chartData = Object.entries(weekly)
      .map(([week, value]) => ({ week, enrollments: value }))
      .sort((a, b) => {
        const A = parseWeekKey(a.week);
        const B = parseWeekKey(b.week);
        return A.year !== B.year ? A.year - B.year : A.week - B.week;
      });

    const conversionRate = screenings > 0 ? (enrollments / screenings * 100).toFixed(1) : "—";
    const failureRate = preScreens > 0 ? (screenFailures / preScreens * 100).toFixed(1) : "—";

    return { enrollments, screenings, followUps, preScreens, screenFailures, conversionRate, failureRate, chartData };
  }, [selectedCoordinator, coordRange]);

  const coordinatorOperationalStats = useMemo(() => {
    if (!selectedCoordinator) return null;
    const coord = selectedCoordinator.firestoreName;

    const process = (items) => {
      const relevant = items.filter(i => i.coordinator === coord);
      let resolvedCount = 0, totalResolutionDays = 0;
      relevant.forEach(i => {
        if (i.resolvedDate) {
          const raised = parseMMDDYYYY(i.raisedDate);
          const resolved = parseMMDDYYYY(i.resolvedDate);
          if (raised && resolved) {
            totalResolutionDays += (resolved - raised) / (1000 * 60 * 60 * 24);
            resolvedCount++;
          }
        }
      });
      return {
        total: relevant.length,
        open: relevant.filter(i => !i.resolvedDate).length,
        avgResolution: resolvedCount > 0 ? (totalResolutionDays / resolvedCount).toFixed(1) : "—",
      };
    };

    return { queries: process(queries), findings: process(findings) };
  }, [selectedCoordinator, queries, findings]);

  // All submissions for the selected coordinator, newest first
  const coordinatorWeeklyReports = useMemo(() => {
    if (!selectedCoordinator) return [];
    return [...selectedCoordinator.reports].sort((a, b) => b.submittedAt - a.submittedAt);
  }, [selectedCoordinator]);

  // Date-range-filtered view of coordinatorWeeklyReports for the Weekly tab
  const filteredWeeklyReports = useMemo(() => {
    const from = weeklyFrom ? new Date(weeklyFrom + "T00:00:00") : null;
    const to = weeklyTo ? new Date(weeklyTo + "T23:59:59") : null;
    return coordinatorWeeklyReports.filter((r) => {
      if (from && r.submittedAt < from) return false;
      if (to && r.submittedAt > to) return false;
      return true;
    });
  }, [coordinatorWeeklyReports, weeklyFrom, weeklyTo]);

  /* ---------------- HANDLERS ---------------- */
  const handleWeekClick = (weekKey) => {
    const data = weekLookup[weekKey];
    if (!data) return;
    setSelectedCoordinator(null);
    setSelectedWeek(data);
  };

  const handleSavePerformanceEntry = async () => {
    if (!entryCoordinator || !entryStudyId || !raisedDate || !entryType) return;

    const payload = {
      coordinator: entryCoordinator,
      studyId: entryStudyId,
      raisedDate: toMMDDYYYY(raisedDate),
      resolvedDate: resolvedDate ? toMMDDYYYY(resolvedDate) : null,
      createdAt: serverTimestamp(),
    };

    const targetCollection = entryType === "QUERY" ? "sponsorQueries" : "monitorFindings";
    await addDoc(collection(db, getCollectionPath(targetCollection)), payload);

    setShowPerfEntry(false);
    setEntryType(null);
    setEntryCoordinator("");
    setEntryStudyId("");
    setRaisedDate("");
    setResolvedDate("");
  };

  const periodOptions = [
    ["THIS_WEEK", "This Week"],
    ["THIS_MONTH", "This Month"],
    ["LAST_3M", "Last 3 Months"],
    ["YTD", "Year to Date"],
    ["ALL", "All Time"],
  ];

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "operational", label: "Operational" },
    { id: "weekly", label: "Weekly" },
  ];

  const inputCls = "w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]";

  /* ---------------- UI ---------------- */
  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 space-y-3">

        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(backTarget(location.state, "/dashboard", "Back").to)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-white" strokeWidth={2.4} />
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  Operations · Analytics
                </div>
                <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                  Reports &amp; Analytics
                </h1>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowPerfEntry(true)}
            className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Plus className="w-3.5 h-3.5 relative" />
            <span className="relative">Performance Entry</span>
          </button>
        </div>

        {/* ── Time-filtered sections (Coordinator Reports first, then the trend) ── */}

        {/* Period filter — applies to Coordinator Reports and the trend only, not to
            the Referral Link Funnel or Queries & Findings further down. */}
        <div className="flex items-center gap-2 pt-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold shrink-0">
            Time period
          </span>
          <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur-xl gap-0.5 flex-wrap">
            {periodOptions.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition ${
                  period === k
                    ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                    : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Coordinator Reports — period-scoped */}
        <SectionHeading
          icon={<Users className="w-4 h-4 text-purple-200" />}
          title="Coordinator Reports"
          hint="Click a coordinator to view their detailed stats and weekly submissions"
        />
        {/* Goal points the admin's rules earned from weekly reports, waiting for approval */}
        <GoalPointApprovals scrollOnLoad={goalPointsFocus} />
        {/* From the 1st: "Close <last month>?" while it's unclosed (admins) */}
        <MonthClosePrompt
          onReview={() => document.getElementById("goal-points")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />

        {/* Patient-level reports (new form): who each coordinator reported, by study */}
        <Card
          title="Submitted reports"
          subtitle="Patient-level weekly reports, newest first. Click one to see its patients."
          icon={<ClipboardList className="w-3.5 h-3.5 text-purple-300" />}
        >
          <SubmittedReportsPanel visibleReportIds={visibleReportIds} />
        </Card>
        <Card>
          {coordinators.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No coordinator data for this period.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {coordinators.map((c) => (
                <button
                  key={c.key}
                  onClick={() => {
                    setSelectedWeek(null);
                    setActiveTab("overview");
                    setWeeklyFrom("");
                    setWeeklyTo("");
                    setSelectedCoordinator(c);
                  }}
                  className="group relative text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-purple-300/30 backdrop-blur-xl p-3.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(139,92,246,0.4)] overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.03] to-blue-500/[0.03] group-hover:from-purple-500/[0.07] group-hover:to-blue-500/[0.07] transition-all pointer-events-none" />
                  <div className="relative flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
                      {(c.displayName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">{c.displayName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-slate-400">
                          {c.weeks} {c.weeks === 1 ? "week" : "weeks"}
                        </span>
                        <span className="text-slate-600">·</span>
                        <TrendBadge trend={c.trend} />
                      </div>
                    </div>
                    <span className="text-slate-500 group-hover:text-purple-300 transition text-sm shrink-0">→</span>
                  </div>
                  <div className="relative mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-1 text-center">
                    {[
                      ["Enrollments", c.enrollments, "text-emerald-300"],
                      ["Screenings", c.screenings, "text-white"],
                      ["Follow-ups", c.followUps, "text-blue-300"],
                    ].map(([label, value, cls]) => (
                      <div key={label}>
                        <div className={`text-sm font-bold ${cls}`}>{value}</div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Site enrollment trend */}
        <Card
          title="Site Enrollment Trend"
          subtitle="Click a week on the X-axis to inspect studies"
          icon={<Activity className="w-3.5 h-3.5 text-purple-300" />}
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={siteTrendData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="siteLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#A78BFA" />
                  <stop offset="100%" stopColor="#60A5FA" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="week"
                stroke={axisStyle.stroke}
                tick={(props) => <ClickableXAxisTick {...props} onClick={handleWeekClick} />}
              />
              <YAxis {...axisStyle} />
              <Tooltip {...tooltipStyles} />
              <Line
                type="monotone"
                dataKey="enrollments"
                stroke="url(#siteLine)"
                strokeWidth={2.5}
                dot={{ fill: "#A78BFA", r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: "#A78BFA", stroke: "rgba(167,139,250,0.4)", strokeWidth: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* ── Sections NOT affected by the time filter above ───────────────── */}

        {/* Referral questionnaire link funnel (additive, read-only). All-time. */}
        <SectionHeading
          icon={<ListChecks className="w-4 h-4 text-purple-200" />}
          title="Referral Link Funnel"
          hint="Sent = unique people first texted in this window · Submitted = completions in this window, whenever they were texted"
        />
        <ReferralLinkFunnelCard />

        {/* Queries & Findings — all-time open/total counts. */}
        <SectionHeading
          icon={<AlertTriangle className="w-4 h-4 text-purple-200" />}
          title="Queries & Findings"
          hint="Open and total counts · all-time"
        />
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <KpiTile
            label="Open Queries"
            value={queries.filter((q) => !q.resolvedDate).length}
            icon={<AlertTriangle className="w-4 h-4 text-white/80" />}
            accent="from-amber-500/20 to-orange-500/20"
            valueClass="text-amber-300"
          />
          <KpiTile
            label="Open Findings"
            value={findings.filter((f) => !f.resolvedDate).length}
            icon={<AlertTriangle className="w-4 h-4 text-white/80" />}
            accent="from-rose-500/20 to-pink-500/20"
            valueClass="text-rose-300"
          />
          <KpiTile
            label="Total Queries"
            value={queries.length}
            icon={<ClipboardList className="w-4 h-4 text-white/80" />}
            accent="from-purple-500/20 to-indigo-500/20"
          />
          <KpiTile
            label="Total Findings"
            value={findings.length}
            icon={<ListChecks className="w-4 h-4 text-white/80" />}
            accent="from-blue-500/20 to-cyan-500/20"
          />
        </section>

        {/* ======== COORDINATOR MODAL ======== */}
        {selectedCoordinator && coordinatorStats && (
          <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-purple-600/20 blur-[140px]" />
                <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-[140px]" />
              </div>

              {/* Header */}
              <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
                  {(selectedCoordinator.displayName || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">Coordinator</div>
                  <h2 className="text-base font-semibold tracking-tight text-white leading-tight truncate">
                    {selectedCoordinator.displayName}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedCoordinator(null)}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Tab bar */}
              <div className="relative px-5 border-b border-white/10 flex items-center gap-1 shrink-0 bg-white/[0.01]">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px ${
                      activeTab === tab.id
                        ? "text-white border-purple-400"
                        : "text-slate-400 hover:text-slate-200 border-transparent"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Body */}
              <div className="relative overflow-y-auto flex-1 p-5 space-y-3">

                {/* ---- OVERVIEW TAB ---- */}
                {activeTab === "overview" && (
                  <>
                    <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 gap-0.5">
                      {[["30", "Last 30 days"], ["90", "Last 90 days"], ["ALL", "All time"]].map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => setCoordRange(k)}
                          className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                            coordRange === k
                              ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                              : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                      <KpiTile label="Enrollments" value={coordinatorStats.enrollments} icon={<CheckCircle2 className="w-4 h-4 text-white/80" />} accent="from-emerald-500/20 to-teal-500/20" valueClass="text-emerald-300" />
                      <KpiTile label="Screenings" value={coordinatorStats.screenings} icon={<Layers className="w-4 h-4 text-white/80" />} accent="from-purple-500/20 to-indigo-500/20" />
                      <KpiTile label="Follow-ups" value={coordinatorStats.followUps} icon={<ListChecks className="w-4 h-4 text-white/80" />} accent="from-blue-500/20 to-cyan-500/20" valueClass="text-blue-300" />
                      <KpiTile label="Pre-screens" value={coordinatorStats.preScreens} icon={<ClipboardList className="w-4 h-4 text-white/80" />} accent="from-violet-500/20 to-purple-500/20" valueClass="text-violet-300" />
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">Conversion Rate</div>
                        <div className="text-xl font-bold text-white">
                          {coordinatorStats.conversionRate === "—" ? "—" : `${coordinatorStats.conversionRate}%`}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Enrollments ÷ Screenings</div>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">Screen Failure Rate</div>
                        <div className="text-xl font-bold text-rose-300">
                          {coordinatorStats.failureRate === "—" ? "—" : `${coordinatorStats.failureRate}%`}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Screen Failures ÷ Pre-screens</div>
                      </div>
                    </div>

                    <Card title="Weekly Enrollment Trend" icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-300" />}>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={coordinatorStats.chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                          <defs>
                            <linearGradient id="coordLine" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#34D399" />
                              <stop offset="100%" stopColor="#22D3EE" />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="week" {...axisStyle} />
                          <YAxis {...axisStyle} />
                          <Tooltip {...tooltipStyles} />
                          <Line
                            type="monotone"
                            dataKey="enrollments"
                            stroke="url(#coordLine)"
                            strokeWidth={2.5}
                            dot={{ fill: "#34D399", r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, fill: "#34D399", stroke: "rgba(52,211,153,0.4)", strokeWidth: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </>
                )}

                {/* ---- OPERATIONAL TAB ---- */}
                {activeTab === "operational" && coordinatorOperationalStats && (
                  <Card
                    title="Operational Load"
                    subtitle="Queries & findings tied to this coordinator"
                    icon={<ClipboardList className="w-3.5 h-3.5 text-amber-300" />}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-md bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
                            <AlertTriangle className="w-3 h-3 text-amber-300" />
                          </div>
                          <h4 className="text-xs font-semibold text-white uppercase tracking-wider">Sponsor Queries</h4>
                        </div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li className="flex justify-between"><span className="text-slate-400">Total</span><span className="font-mono text-white">{coordinatorOperationalStats.queries.total}</span></li>
                          <li className="flex justify-between"><span className="text-slate-400">Open</span><span className="font-mono text-amber-300">{coordinatorOperationalStats.queries.open}</span></li>
                          <li className="flex justify-between"><span className="text-slate-400">Avg Resolution</span><span className="font-mono text-white">{coordinatorOperationalStats.queries.avgResolution} days</span></li>
                        </ul>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-md bg-rose-500/15 border border-rose-400/30 flex items-center justify-center">
                            <ListChecks className="w-3 h-3 text-rose-300" />
                          </div>
                          <h4 className="text-xs font-semibold text-white uppercase tracking-wider">Monitor Findings</h4>
                        </div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li className="flex justify-between"><span className="text-slate-400">Total</span><span className="font-mono text-white">{coordinatorOperationalStats.findings.total}</span></li>
                          <li className="flex justify-between"><span className="text-slate-400">Open</span><span className="font-mono text-rose-300">{coordinatorOperationalStats.findings.open}</span></li>
                          <li className="flex justify-between"><span className="text-slate-400">Avg Resolution</span><span className="font-mono text-white">{coordinatorOperationalStats.findings.avgResolution} days</span></li>
                        </ul>
                      </div>
                    </div>
                  </Card>
                )}

                {/* ---- WEEKLY TAB ---- */}
                {activeTab === "weekly" && (
                  <div className="space-y-3">

                    {/* Date range filter */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2">Filter by date range</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-[10px] text-slate-500 mb-1">From</p>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                            <input
                              type="date"
                              value={weeklyFrom}
                              onChange={(e) => setWeeklyFrom(e.target.value)}
                              className={inputCls}
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 mb-1">To</p>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                            <input
                              type="date"
                              value={weeklyTo}
                              onChange={(e) => setWeeklyTo(e.target.value)}
                              className={inputCls}
                            />
                          </div>
                        </div>
                      </div>
                      {(weeklyFrom || weeklyTo) && (
                        <button
                          onClick={() => { setWeeklyFrom(""); setWeeklyTo(""); }}
                          className="mt-2 text-[10px] text-purple-300 hover:text-purple-200 transition"
                        >
                          Clear filter
                        </button>
                      )}
                    </div>

                    {/* Report count indicator */}
                    <p className="text-[11px] text-slate-400 px-0.5">
                      Showing {filteredWeeklyReports.length} of {coordinatorWeeklyReports.length} {coordinatorWeeklyReports.length === 1 ? "submission" : "submissions"}
                    </p>

                    {filteredWeeklyReports.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">
                        {coordinatorWeeklyReports.length === 0 ? "No weekly reports found." : "No submissions in the selected date range."}
                      </p>
                    ) : (
                      filteredWeeklyReports.map((report, idx) => {
                        const weekKey = `${report.submittedAt.getFullYear()}-W${getISOWeek(report.submittedAt)}`;
                        const { year, week } = parseWeekKey(weekKey);
                        const { start, end } = getDateRangeFromISOWeek(year, week);
                        return (
                          <div key={idx} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2 flex-wrap">
                              <CalendarDays className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                              <span className="text-xs font-semibold text-white">{weekKey}</span>
                              <span className="text-[11px] text-slate-400">
                                · {formatDate(start)} – {formatDate(end)}
                              </span>
                              <span className="ml-auto text-[10px] text-slate-500">
                                Submitted {formatDate(report.submittedAt)}
                              </span>
                            </div>
                            {/* Saved with NO coordinator name (early reports).
                                An admin can credit it to someone — stored in new
                                fields; the original blank name is never touched. */}
                            {!hasOwnCoordinatorName(report) && report.__docId && (
                              <div className="px-4 py-2 border-b border-white/5 bg-amber-500/[0.06] text-[11px] text-amber-100">
                                {report.assignedCoordinator ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span>
                                      No name on this report · assigned to{" "}
                                      <span className="font-semibold text-white">{report.assignedCoordinator}</span>
                                      {report.assignedCoordinatorBy ? ` by ${report.assignedCoordinatorBy}` : ""}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => undoAssignment(report)}
                                      className="ml-auto underline text-amber-200 hover:text-white"
                                    >
                                      Undo
                                    </button>
                                  </div>
                                ) : assigningId === report.__docId ? (
                                  <div className="space-y-2">
                                    <div>No name was saved on this report. Who filed it?</div>
                                    {assignFreeText ? (
                                      <input
                                        type="text"
                                        value={assignTyped}
                                        onChange={(e) => setAssignTyped(e.target.value)}
                                        placeholder="Type the coordinator's name"
                                        className="w-full sm:w-80 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50"
                                      />
                                    ) : (
                                      <div className="w-full sm:w-80">
                                        <SearchableSelect
                                          value={assignPick}
                                          options={allCoordinatorNames}
                                          onChange={setAssignPick}
                                          placeholder="Type to find a coordinator (active or not)"
                                          noMatchText="No coordinator matches. Use “Type a name instead”."
                                        />
                                      </div>
                                    )}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <button
                                        type="button"
                                        onClick={() => saveAssignment(report)}
                                        disabled={assignBusy || !(assignFreeText ? assignTyped : assignPick).trim()}
                                        className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold disabled:opacity-50"
                                      >
                                        {assignBusy ? "Saving…" : "Assign"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setAssignFreeText((v) => !v)}
                                        className="underline text-amber-200 hover:text-white"
                                      >
                                        {assignFreeText ? "Pick from the coordinator list" : "Type a name instead"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setAssigningId(null)}
                                        className="text-slate-400 hover:text-white"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                    {assignError && <div className="text-rose-300">{assignError}</div>}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span>No coordinator name was saved on this report.</span>
                                    <button
                                      type="button"
                                      onClick={() => startAssign(report)}
                                      className="ml-auto underline text-amber-200 hover:text-white"
                                    >
                                      Assign coordinator
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-white/5">
                                    <th className="px-4 py-2">Study</th>
                                    <th className="px-3 py-2 text-center">Pre-screens</th>
                                    <th className="px-3 py-2 text-center">Screen Fail</th>
                                    <th className="px-3 py-2 text-center">Screenings</th>
                                    <th className="px-3 py-2 text-center">Enrollments</th>
                                    <th className="px-3 py-2 text-center">Follow-ups</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(report.studies || []).map((s, si) => (
                                    <tr key={si} className="border-t border-white/5 hover:bg-white/[0.02] transition">
                                      <td className="px-4 py-2">
                                        <span className="font-mono text-purple-300/80 mr-1.5">{s.studyId}</span>
                                        <span className="text-slate-300">{s.name}</span>
                                      </td>
                                      <td className="px-3 py-2 text-center text-slate-300 font-mono">{s.preScreens || 0}</td>
                                      <td className="px-3 py-2 text-center text-rose-300 font-mono">{s.screenFailures || 0}</td>
                                      <td className="px-3 py-2 text-center text-slate-200 font-mono">{s.screenings || 0}</td>
                                      <td className="px-3 py-2 text-center text-emerald-300 font-bold font-mono">{s.enrollments || 0}</td>
                                      <td className="px-3 py-2 text-center text-blue-300 font-mono">{s.followUps || 0}</td>
                                    </tr>
                                  ))}
                                  {(reportPeriodCounts(report).preScreens > 0 || reportPeriodCounts(report).followUps > 0) && (
                                    <tr className="border-t border-white/5">
                                      <td className="px-4 py-2 text-slate-400 italic">Not tied to a study</td>
                                      <td className="px-3 py-2 text-center text-slate-300 font-mono">{reportPeriodCounts(report).preScreens}</td>
                                      <td className="px-3 py-2 text-center text-slate-500 font-mono">—</td>
                                      <td className="px-3 py-2 text-center text-slate-500 font-mono">—</td>
                                      <td className="px-3 py-2 text-center text-slate-500 font-mono">—</td>
                                      <td className="px-3 py-2 text-center text-blue-300 font-mono">{reportPeriodCounts(report).followUps}</td>
                                    </tr>
                                  )}
                                  {(!report.studies || report.studies.length === 0) && (
                                    <tr>
                                      <td colSpan={6} className="px-4 py-4 text-center text-slate-500 text-xs">No studies logged this week.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

        {/* ======== WEEK SNAPSHOT MODAL ======== */}
        {selectedWeek && (
          <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
              <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                  <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">Week Snapshot</div>
                  <h2 className="text-base font-semibold tracking-tight text-white leading-tight truncate">
                    {formatDate(selectedWeek.range.start)} – {formatDate(selectedWeek.range.end)}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedWeek(null)}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative overflow-y-auto flex-1 p-5 space-y-3">
                <div className="rounded-xl border border-white/10 bg-gradient-to-r from-purple-500/10 to-blue-500/10 px-3 py-2.5 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">Total Enrollments</span>
                  <span className="text-xl font-bold text-white tracking-tight">{selectedWeek.totalEnrollments}</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-400 border-b border-white/5">
                        <th className="px-3 py-2">Study</th>
                        <th className="px-3 py-2 text-center">Enrollments</th>
                        <th className="px-3 py-2 text-center">Follow-ups</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWeek.studies.map((s, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/[0.03] transition">
                          <td className="px-3 py-2 text-slate-200">
                            <span className="font-mono text-purple-300/80 mr-2">{s.studyId}</span>{s.name}
                          </td>
                          <td className="px-3 py-2 text-center text-emerald-300 font-semibold">{s.enrollments}</td>
                          <td className="px-3 py-2 text-center text-blue-300 font-semibold">{s.followUps}</td>
                        </tr>
                      ))}
                      {selectedWeek.studies.length === 0 && (
                        <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400">No studies for this week.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ======== PERFORMANCE ENTRY MODAL ======== */}
        {showPerfEntry && (
          <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-md max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-purple-600/20 blur-[120px]" />
                <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-600/20 blur-[120px]" />
              </div>

              <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                  <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" strokeWidth={2.4} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">Operations</div>
                  <h2 className="text-base font-semibold tracking-tight text-white leading-tight">Performance Entry</h2>
                </div>
                <button
                  onClick={() => { setShowPerfEntry(false); setEntryType(null); }}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="relative overflow-y-auto flex-1 p-5 space-y-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Coordinator</span>
                  {/* Type to filter, then pick — active coordinators only, same
                      list as before; the saved value is always a listed name. */}
                  <SearchableSelect
                    value={entryCoordinator}
                    options={coordinatorList}
                    onChange={setEntryCoordinator}
                    placeholder="Type to find a coordinator"
                    noMatchText="No active coordinator matches that."
                  />
                </label>

                {entryCoordinator && (
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Study</span>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                      <select
                        value={entryStudyId}
                        onChange={(e) => setEntryStudyId(e.target.value)}
                        className="w-full appearance-none pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
                      >
                        <option value="" className="bg-[#0b0820] text-slate-100">Select Study</option>
                        {allStudies.map((s) => (
                          <option key={s.studyId} value={s.studyId} className="bg-[#0b0820] text-slate-100">{s.studyId}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </label>
                )}

                {entryStudyId && (
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Entry Type</span>
                    <div className="grid grid-cols-2 gap-2">
                      {["QUERY", "FINDING"].map((type) => (
                        <button
                          key={type}
                          onClick={() => setEntryType(type)}
                          className={`px-3 py-2 rounded-xl text-xs font-semibold transition border ${
                            entryType === type
                              ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-purple-300/40 text-white shadow-[inset_0_0_20px_rgba(139,92,246,0.2)]"
                              : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08] hover:text-white"
                          }`}
                        >
                          {type === "QUERY" ? "Sponsor Query" : "Monitor Finding"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {entryType && (
                  <div className="space-y-3">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        {entryType === "QUERY" ? "Query Raised Date" : "Finding Raised Date"}
                      </span>
                      <p className="text-[10px] text-slate-500 mb-1">Date when the issue was first received</p>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="date"
                          value={raisedDate}
                          onChange={(e) => setRaisedDate(e.target.value)}
                          className={inputCls}
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Resolution Date</span>
                      <p className="text-[10px] text-slate-500 mb-1">Leave empty if still unresolved</p>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="date"
                          value={resolvedDate}
                          onChange={(e) => setResolvedDate(e.target.value)}
                          className={inputCls}
                        />
                      </div>
                    </label>
                  </div>
                )}
              </div>

              <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
                <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
                  <Sparkles className="w-3 h-3 text-purple-300" />
                  New record
                </div>
                <button
                  onClick={() => { setShowPerfEntry(false); setEntryType(null); }}
                  className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePerformanceEntry}
                  className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Save className="w-4 h-4 relative" />
                  <span className="relative">Save</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="h-6" />
      </div>
    </div>
  );
};

export default AdminReportsPage;
